import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { ClipboardCheck, CheckCircle2, XCircle, Loader2, RotateCcw, ChevronRight } from "lucide-react";

// Fisher-Yates: returns a shuffled [0..n-1] index array (used for presentation only).
const shuffledIndices = (n) => {
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const buildOptionOrders = (questions) => {
  const oo = {};
  questions.forEach((q, i) => { oo[i] = shuffledIndices((q.options || []).length); });
  return oo;
};

export const LessonQuiz = ({ lessonId, onPassed }) => {
  const [quiz, setQuiz] = useState(null);
  const [order, setOrder] = useState([]);
  const [optOrder, setOptOrder] = useState({});
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setResult(null); setAnswers({}); setLoading(true);
    api.get(`/lessons/${lessonId}/quiz`)
      .then((r) => {
        setQuiz(r.data);
        setOrder(shuffledIndices(r.data.questions.length));
        setOptOrder(buildOptionOrders(r.data.questions));
      })
      .catch(() => setQuiz(null))
      .finally(() => setLoading(false));
  }, [lessonId]);

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  if (!quiz) return null;

  const allAnswered = quiz.questions.every((_, i) => answers[i] !== undefined);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/lessons/${lessonId}/quiz/submit`, {
        answers: quiz.questions.map((_, i) => (answers[i] === undefined ? -1 : answers[i])),
      });
      setResult(data);
      if (data.passed) toast.success(`Passed — ${data.percent}%! Lesson complete.`);
      else toast.error(`${data.percent}% — you need ${data.pass_threshold}% to pass. Review and try again.`);
    } catch (e) {
      toast.error("Could not submit your answers. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const retry = () => {
    setResult(null); setAnswers({});
    if (quiz) { setOrder(shuffledIndices(quiz.questions.length)); setOptOrder(buildOptionOrders(quiz.questions)); }
  };

  return (
    <div className="mt-8 pt-6 border-t border-border/60 rounded-sm" data-testid="lesson-quiz">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardCheck className="h-4 w-4 text-accent" />
        <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent">Knowledge Check</span>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        Answer all {quiz.questions.length} questions. Score {quiz.pass_threshold}% or higher to complete this lesson and unlock the next.
      </p>

      <div className="space-y-5">
        {order.map((qi, displayIdx) => {
          const q = quiz.questions[qi];
          const res = result?.results?.[qi];
          return (
            <div key={qi} className="rounded-sm border border-border/70 bg-card p-4" data-testid={`lesson-quiz-q-${qi}`}>
              <div className="font-medium text-sm mb-3">{displayIdx + 1}. {q.q}</div>
              <div className="space-y-2">
                {(optOrder[qi] || q.options.map((_, k) => k)).map((origIdx) => {
                  const opt = q.options[origIdx];
                  const selected = answers[qi] === origIdx;
                  let cls = "border-border/70 hover:border-primary/40";
                  if (result) {
                    if (origIdx === res.correct_index) cls = "border-primary/60 bg-primary/10 text-primary";
                    else if (origIdx === res.chosen && !res.is_correct) cls = "border-rose-500/50 bg-rose-500/10 text-rose-300";
                    else cls = "border-border/50 opacity-70";
                  } else if (selected) {
                    cls = "border-primary/60 bg-primary/10 text-primary";
                  }
                  return (
                    <button key={origIdx} type="button" disabled={!!result}
                      onClick={() => setAnswers((a) => ({ ...a, [qi]: origIdx }))}
                      data-testid={`lesson-quiz-q-${qi}-opt-${origIdx}`}
                      className={`w-full text-left text-sm px-3 py-2.5 rounded-sm border transition-colors flex items-center gap-2 ${cls}`}>
                      {result && origIdx === res.correct_index && <CheckCircle2 className="h-4 w-4 shrink-0" />}
                      {result && origIdx === res.chosen && !res.is_correct && <XCircle className="h-4 w-4 shrink-0" />}
                      <span>{opt}</span>
                    </button>
                  );
                })}
              </div>
              {result && !res.is_correct && res.explanation && (
                <div className="mt-3 text-xs text-slate-300 bg-secondary/40 border border-border/60 rounded-sm p-3" data-testid={`lesson-quiz-explain-${qi}`}>
                  <span className="text-accent font-mono uppercase tracking-widest text-[10px]">Why</span>
                  <p className="mt-1 leading-relaxed">{res.explanation}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!result ? (
        <Button onClick={submit} disabled={!allAnswered || submitting} data-testid="lesson-quiz-submit"
          className="mt-5 bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm">
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Submit Knowledge Check
        </Button>
      ) : (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className={`mt-5 rounded-sm border p-4 flex flex-wrap items-center justify-between gap-3 ${
            result.passed ? "border-primary/40 bg-primary/5" : "border-rose-500/40 bg-rose-500/5"}`}
          data-testid={result.passed ? "lesson-quiz-pass" : "lesson-quiz-fail"}>
          <div className="flex items-center gap-2">
            {result.passed ? <CheckCircle2 className="h-5 w-5 text-primary" /> : <XCircle className="h-5 w-5 text-rose-400" />}
            <span className="font-display font-bold">
              {result.passed ? `Passed — ${result.percent}%` : `${result.percent}% — need ${result.pass_threshold}%`}
            </span>
            <span className="text-xs text-muted-foreground">({result.correct}/{result.total} correct)</span>
          </div>
          {result.passed ? (
            <Button onClick={() => onPassed(result)} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm" data-testid="lesson-quiz-continue">
              Continue <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={retry} variant="outline" className="rounded-sm" data-testid="lesson-quiz-retry">
              <RotateCcw className="mr-1.5 h-4 w-4" /> Try Again
            </Button>
          )}
        </motion.div>
      )}
    </div>
  );
};
